// ════════════════════════════════════════════════════════════════════════
//  Verlängert, aber nicht abgerechnet
//
//  Die Liste, die es überhaupt erst nötig macht, `invoiced_at` zu führen:
//  `auto_renew` verlängert eine Lizenz, ohne zu wissen, ob jemand bezahlt hat.
//  Automatisch verlängerte Zeiträume landen hier von selbst, weil der Job
//  `invoiced_at` nicht setzt.
//
//  Abrechnen ist ein Häkchen. Die Plattform stellt keine Rechnung, sie merkt
//  sich nur, dass es passiert ist - führend bleibt die Buchhaltung.
// ════════════════════════════════════════════════════════════════════════

import { Link } from "react-router-dom";
import { useSession } from "@otto/plattform";
import { useMarkInvoiced, useUninvoiced } from "../lib/data.js";
import { formatDate, formatEuro, todayInBerlin } from "../lib/license.js";
import shell from "../../../shared/components/shell.module.css";
import styles from "./schools.module.css";

export function Billing() {
  const { client } = useSession();
  const open = useUninvoiced(client);
  const mark = useMarkInvoiced(client);

  const total = (open.data ?? []).reduce(
    (sum, license) => sum + (license.priceCents ?? 0),
    0,
  );
  const withoutPrice = (open.data ?? []).filter(
    (license) => license.priceCents === null,
  ).length;

  return (
    <div className={shell.card}>
      <p className={styles.hint}>
        <Link to="/schools">← Alle Schulen</Link>
      </p>
      <h1 className={shell.title}>Offene Abrechnungen</h1>
      <p className={shell.subtitle}>
        Lizenzzeiträume ohne Abrechnungsvermerk – über alle Schulen. Das Häkchen setzt nur
        den Vermerk; die Rechnung entsteht in der Buchhaltung.
      </p>

      {open.isPending && <p className={shell.empty}>Einen Moment …</p>}
      {open.isError && (
        <div className={`${shell.notice} ${shell.noticeError}`}>
          Die Liste war nicht abzurufen: {open.error.message}
        </div>
      )}
      {open.data?.length === 0 && (
        <p className={shell.empty}>Alles abgerechnet. Nichts zu tun.</p>
      )}

      {open.data && open.data.length > 0 && (
        <>
          <div className={styles.scroll}>
            <table className={shell.wide}>
              <thead>
                <tr>
                  <th className={shell.stretch}>Schule</th>
                  <th>Von</th>
                  <th>Bis</th>
                  <th>Herkunft</th>
                  <th className={styles.numeric}>Betrag</th>
                  <th>Abgerechnet</th>
                </tr>
              </thead>
              <tbody>
                {open.data.map((license) => (
                  <tr key={license.id}>
                    <td className={shell.stretch}>
                      <Link to={`/schools/${license.schoolId}`}>
                        {license.schoolName}
                      </Link>
                    </td>
                    <td>{formatDate(license.validFrom)}</td>
                    <td>{formatDate(license.validUntil)}</td>
                    <td>
                      {license.source === "auto" ? (
                        <span className={styles.auto}>automatisch verlängert</span>
                      ) : (
                        "verkauft"
                      )}
                    </td>
                    <td className={styles.numeric}>{formatEuro(license.priceCents)}</td>
                    <td>
                      <div className={styles.checkbox}>
                        {/* Ungesteuert und nicht `checked={false}`: sonst
                            kämpft React gegen den Klick und das Häkchen
                            erscheint nie. Nach dem Vermerk verschwindet die
                            Zeile ohnehin - bis dahin soll man sehen, dass der
                            Klick angekommen ist. */}
                        <input
                          id={`invoiced-${license.id}`}
                          type="checkbox"
                          defaultChecked={false}
                          disabled={mark.isPending}
                          onChange={() =>
                            mark.mutate({
                              id: license.id,
                              invoicedAt: todayInBerlin(),
                            })
                          }
                        />
                        <label htmlFor={`invoiced-${license.id}`}>abgerechnet</label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className={`${styles.sum} ${shell.spaced}`}>
            Summe: {formatEuro(total)}
            {withoutPrice > 0 && (
              <span className={styles.warn}>
                {" "}
                – {withoutPrice} Zeitraum
                {withoutPrice === 1 ? "" : "räume"} ohne Betrag, nicht in der Summe
              </span>
            )}
          </p>

          {mark.isError && (
            <div className={`${shell.notice} ${shell.noticeError}`}>
              Der Vermerk war nicht zu setzen: {mark.error.message}
            </div>
          )}
        </>
      )}
    </div>
  );
}
